import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  FinnairBoundItem,
  FinnairDisruptedBoundItem,
  FinnairItineraryItemFlight,
  FinnairLocation,
  FinnairPassengerCode,
  FinnairPassengerItem,
} from '@fcom/dapi/api/models';
import { FinnairThemeService } from '@fcom/common';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { isByBusOnly, isByTrainOnly } from '../../utils/common-booking.utils';
@Component({
  selector: 'fin-flight-card',
  templateUrl: './flight-card.component.html',
  styleUrls: ['./flight-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlightCardComponent implements OnChanges {
  private themeService = inject(FinnairThemeService);

  readonly IconLibrary = IconLibrary;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  @Input()
  flightOrBound: FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight;
  @Input()
  title: string;
  @Input()
  dim = false;
  @Input()
  locations?: Record<string, FinnairLocation>;
  @Input()
  passengers: FinnairPassengerItem[];
  @Input()
  hideModify = false;
  @Input()
  hideViewDetails = false;
  @Input()
  expanderOpen: boolean;
  @Input()
  roundedBottom = true;
  @Input()
  iconClasses: string;
  @Input()
  capitalizeTitle = true;

  cardExpanderOpen: boolean;
  paxIcon: SvgLibraryIcon;
  flightIcon: SvgLibraryIcon;

  @Output()
  modifyClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  viewDetails: EventEmitter<void> = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['passengers']) {
      this.paxIcon = this.getPaxNumberIcon();
    }

    if (changes['expanderOpen']) {
      this.cardExpanderOpen = changes['expanderOpen'].currentValue;
    }

    if (changes['flightOrBound']) {
      this.flightIcon = this.getFlightIcon();
    }
  }

  toggleOpenCard(isOpen: boolean): void {
    this.cardExpanderOpen = !isOpen;
  }

  private getPaxNumberIcon(): SvgLibraryIcon {
    if (this.passengers.length === 1) {
      return SvgLibraryIcon.SINGLE_PAX;
    }

    return this.passengers.some(({ passengerTypeCode }) => passengerTypeCode === FinnairPassengerCode.INF)
      ? SvgLibraryIcon.ADULT_INFANT
      : SvgLibraryIcon.MULTI_PAX;
  }

  private getFlightIcon(): SvgLibraryIcon {
    if (isByBusOnly(this.flightOrBound)) {
      return this.isNewBrand() ? SvgLibraryIcon.BUS : SvgLibraryIcon.BUS_BLOCK;
    }

    if (isByTrainOnly(this.flightOrBound)) {
      return this.isNewBrand() ? SvgLibraryIcon.TRAIN : SvgLibraryIcon.TRAIN_BLOCK;
    }

    return this.isNewBrand() ? SvgLibraryIcon.AIRCRAFT_HORIZONTAL : SvgLibraryIcon.AIRCRAFT_HORIZONTAL_BLOCK;
  }
}
